import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import "./_PropertyStatusManager.scss";

const PropertyStatusManager = () => {
  const [propertyStatusAdmin, setPropertyStatusAdmin] = useState([]);
  const [propertyStatus, setPropertyStatus] = useState({
    name: { az: "", en: "", ru: "" },
    iconType: "",
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    fetchPropertyStatuses();
  }, []);

  const fetchPropertyStatuses = () => {
    axiosWithAuth()
      .get("property-statuses/admin")
      .then((response) => {
        setPropertyStatusAdmin(response.data);
      })
      .catch((error) => console.error("Error fetching property statuses:", error));
  };

  const handleInput = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (type === "checkbox") {
      setPropertyStatus((prev) => ({
        ...prev,
        status: checked,
      }));
    } else if (name === "iconType") {
      setPropertyStatus((prev) => ({
        ...prev,
        iconType: value,
      }));
    } else {
      setPropertyStatus((prev) => ({
        ...prev,
        name: {
          ...prev.name,
          [name]: value,
        },
      }));
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const response = await axiosWithAuth().put(
        `property-statuses/${id}`,
        { status: !currentStatus },
      );
      
      if (response.data) {
        fetchPropertyStatuses();
        swal({
          title: "",
          text: "Status has been changed",
          icon: "success",
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      swal({
        title: "",
        text: "Status has not been changed",
        icon: "error",
        timer: 1500,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (isEditing) {
        response = await axiosWithAuth().put(
          `property-statuses/${editId}`,
          propertyStatus
        );
      } else {
        response = await axiosWithAuth().post(
          "property-statuses",
          propertyStatus
        );
      }
      
      if (response.data) {
        setPropertyStatus({
          name: {
            az: "",
            en: "",
            ru: "",
          },
          status: true,
        });
        setIsEditing(false);
        setEditId(null);
        fetchPropertyStatuses();
        swal({
          title: "",
          text: "Property status has been added",
          icon: "success",
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error submitting property status:", error);
      swal({
        title: "",
        text: "Property status has not been added",
        icon: "error",
        timer: 1500,
      });
    }
  };

  const handleEdit = (status) => {
    setIsEditing(true);
    setEditId(status.id);
    setPropertyStatus({
      name: status.name,
      iconType: status.iconType,
      status: status.status,
    });
  };

  const handleDelete = async (id) => {
    try {
      const confirmResult = await swal({
        title: "Are you sure?",
        text: "Are you sure you want to delete this property status?",
        icon: "warning",
        buttons: ["Cancel", "Yes, Delete"],
        dangerMode: true,
      });
      
      if (confirmResult) {
        let url = `property-statuses/${id}`;
        
        let response = await axiosWithAuth().delete(url);
        
        if (response.data) {
          fetchPropertyStatuses();
          swal({
            title: "",
            text: "Property status has been deleted",
            icon: "success",
            timer: 1500,
          });
        }
      }
    } catch (error) {
      console.error("Error deleting property status:", error);
      
      // Check if error suggests a force delete is needed
      if (error.response && error.response.status === 409) {
        const willForce = await swal({
          title: "This status is in use",
          text: "This status is used by properties. Would you like to force delete?",
          icon: "warning",
          buttons: ["Cancel", "Yes, Force Delete"],
          dangerMode: true,
        });
        
        if (willForce) {
          try {
            const forceResponse = await axiosWithAuth().delete(
              `property-statuses/${id}?force=true`
            );
            
            if (forceResponse.data) {
              fetchPropertyStatuses();
              swal({
                title: "",
                text: "Property status has been deleted",
                icon: "success",
                timer: 1500,
              });
            }
          } catch (forceError) {
            console.error("Error force deleting property status:", forceError);
            swal({
              title: "",
              text: "Unable to delete. It may be in use by properties.",
              icon: "error",
              timer: 1500,
            });
          }
        }
      } else {
        swal({
          title: "",
          text: "Property status has not been deleted",
          icon: "error",
          timer: 1500,
        });
      }
    }
  };

  return (
    <div className="property-status-manager">
      <h2>Property Status Manager</h2>
      
      <div className="form-section">
        <h3>{isEditing ? 'Edit Property Status' : 'Add New Property Status'}</h3>
        
        <div className="form-group">
          <label>Name (AZ)</label>
          <input
            type="text"
            name="az"
            value={propertyStatus.name.az}
            onChange={handleInput}
            placeholder="Enter name in Azerbaijani"
            required
          />
        </div>
        <div className="form-group">
          <label>Name (EN)</label>
          <input
            type="text"
            name="en"
            value={propertyStatus.name.en}
            onChange={handleInput}
            placeholder="Enter name in English"
            required
          />
        </div>
        <div className="form-group">
          <label>Name (RU)</label>
          <input
            type="text"
            name="ru"
            value={propertyStatus.name.ru}
            onChange={handleInput}
            placeholder="Enter name in Russian"
            required
          />
        </div>

        <div className="form-group">
          <label>Icon Type</label>
          <input
            type="text"
            name="iconType"
            value={propertyStatus.iconType}
            onChange={handleInput}
            placeholder="Enter Bootstrap icon class (e.g., bi-house)"
            required
          />
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            checked={propertyStatus.status}
            onChange={handleInput}
            name="status"
            id="isActive"
          />
          <label htmlFor="isActive">Active</label>
        </div>

        <div className="button-group">
          <button className="submit-btn" onClick={handleSubmit}>
            {isEditing ? 'Update' : 'Add'} Property Status
          </button>
          {isEditing && (
            <button className="cancel-btn" onClick={() => {
              setIsEditing(false);
              setEditId(null);
              setPropertyStatus({
                name: { az: "", en: "", ru: "" },
                iconType: "",
                status: true,
              });
            }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="table-section">
        <table>
          <thead>
            <tr>
              <th>Name (AZ)</th>
              <th>Name (EN)</th>
              <th>Name (RU)</th>
              <th>Icon Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {propertyStatusAdmin.filter(status => status.status).map((status) => (
              <tr key={status.id}>
                <td>{status.name.az}</td>
                <td>{status.name.en}</td>
                <td>{status.name.ru}</td>
                <td><i className={status.iconType}></i> {status.iconType}</td>
                <td>
                  <span
                    className={`status-badge ${status.status ? "active" : "inactive"}`}
                    onClick={() => handleStatusToggle(status.id, status.status)}
                  >
                    {status.status ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(status)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(status.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PropertyStatusManager;
